import type { Json } from "~/api/tables";

export type InvoiceLike = {
  due_date: string | null;
  paid_at: string | null;
  status: string;
  total_cents: number;
};

export type ReportPeriod = "all" | "month" | "week" | "custom";
export type TimeState = "all" | "billable" | "unbilled" | "invoiced";
export type DateRange = {
  end?: Date;
  start?: Date;
};
export type ReportViewFilters = {
  clientFilter: string;
  customEnd: string;
  customStart: string;
  period: ReportPeriod;
  projectFilter: string;
  timeState: TimeState;
};

export function startOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

export function startOfWeek() {
  const now = new Date();
  const day = now.getDay() || 7;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(now.getDate() - day + 1);
  return start;
}

export function endOfDay(value: string) {
  const date = new Date(value);
  date.setHours(23, 59, 59, 999);
  return date;
}

export function periodRange(
  period: ReportPeriod,
  customStart: string,
  customEnd: string,
): DateRange {
  if (period === "week") return { start: startOfWeek() };
  if (period === "month") return { start: startOfMonth() };
  if (period !== "custom") return {};
  return {
    start: customStart ? new Date(customStart) : undefined,
    end: customEnd ? endOfDay(customEnd) : undefined,
  };
}

export function isReportPeriod(value: unknown): value is ReportPeriod {
  return (
    value === "all" ||
    value === "month" ||
    value === "week" ||
    value === "custom"
  );
}

export function isTimeState(value: unknown): value is TimeState {
  return (
    value === "all" ||
    value === "billable" ||
    value === "unbilled" ||
    value === "invoiced"
  );
}

export function parseReportViewFilters(value: Json): ReportViewFilters | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!isReportPeriod(record.period) || !isTimeState(record.timeState)) {
    return null;
  }
  return {
    clientFilter:
      typeof record.clientFilter === "string" ? record.clientFilter : "",
    customEnd: typeof record.customEnd === "string" ? record.customEnd : "",
    customStart:
      typeof record.customStart === "string" ? record.customStart : "",
    period: record.period,
    projectFilter:
      typeof record.projectFilter === "string" ? record.projectFilter : "",
    timeState: record.timeState,
  };
}

export function isWithinRange(isoDate: string | null, range: DateRange) {
  if (!range.start && !range.end) return true;
  if (!isoDate) return false;
  const time = new Date(isoDate).getTime();
  if (range.start && time < range.start.getTime()) return false;
  if (range.end && time > range.end.getTime()) return false;
  return true;
}

export function invoiceMatchesRange(
  invoice: {
    due_date: string | null;
    issue_date: string;
    paid_at: string | null;
  },
  range: DateRange,
) {
  if (!range.start && !range.end) return true;
  return (
    isWithinRange(invoice.issue_date, range) ||
    isWithinRange(invoice.paid_at, range) ||
    isWithinRange(invoice.due_date, range)
  );
}

export function formatMoney(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

export function exportMoney(cents: number | null | undefined) {
  return ((cents ?? 0) / 100).toFixed(2);
}

export function formatHours(seconds: number) {
  const hours = seconds / 3600;
  return `${hours.toFixed(hours >= 10 ? 0 : 1)}h`;
}

export function exportHours(seconds: number) {
  return (seconds / 3600).toFixed(2);
}

export function exportDateStamp() {
  return new Date().toISOString().slice(0, 10);
}

export function billableValue(entry: {
  duration_seconds: number;
  hourly_rate_cents: number | null;
}) {
  if (!entry.hourly_rate_cents) return 0;
  return Math.round((entry.duration_seconds / 3600) * entry.hourly_rate_cents);
}

export function isOutstanding(invoice: InvoiceLike) {
  return !invoice.paid_at && invoice.status !== "void";
}

export function isOverdue(invoice: InvoiceLike) {
  if (!isOutstanding(invoice) || !invoice.due_date) return false;
  const due = new Date(invoice.due_date);
  due.setHours(23, 59, 59, 999);
  return due.getTime() < Date.now();
}

export function agingBucket(
  invoices: InvoiceLike[],
  minDays: number,
  maxDays?: number,
) {
  const now = Date.now();
  return invoices.reduce((total, invoice) => {
    if (!isOverdue(invoice) || !invoice.due_date) return total;
    const days = Math.floor(
      (now - new Date(invoice.due_date).getTime()) / 86_400_000,
    );
    if (days < minDays) return total;
    if (maxDays !== undefined && days > maxDays) return total;
    return total + invoice.total_cents;
  }, 0);
}
